import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';

import { ENDPOINTS } from '@shared/constants';
import { generateURL } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class EmailSuggestionService {
  private cache = new Map<string, string[]>();

  constructor(private http: HttpClient) {}

  /**
   * Search email suggestions from backend with local memory caching
   */
  getSuggestions(search?: string, limit = 10): Observable<string[]> {
    const query = (search || '').trim().toLowerCase();
    const cacheKey = `${query}_${limit}`;

    if (this.cache.has(cacheKey)) {
      return of(this.cache.get(cacheKey)!);
    }

    const url = generateURL({
      endpoint: ENDPOINTS.EMAIL_SUGGESTIONS_SEARCH,
    });

    let params = new HttpParams().set('limit', limit.toString());
    if (query) {
      params = params.set('search', query);
    }

    return this.http.get<any>(url, { params }).pipe(
      map((res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (res && typeof res === 'object') {
          const keys = Object.keys(res).filter((k) => !isNaN(Number(k)));
          if (keys.length > 0) {
            return keys
              .sort((a, b) => Number(a) - Number(b))
              .map((k) => res[k])
              .filter((val) => typeof val === 'string');
          }
        }
        return [];
      }),
      tap((results) => {
        this.cache.set(cacheKey, results || []);
      })
    );
  }

  /**
   * Save a newly typed/selected email address
   */
  saveEmail(email: string): Observable<{ success: boolean }> {
    if (!email || !this.isValidEmail(email)) {
      return of({ success: false });
    }

    const normalizedEmail = email.trim().toLowerCase();
    const url = generateURL({
      endpoint: ENDPOINTS.EMAIL_SUGGESTIONS_SAVE,
    });

    // Invalidate search cache so subsequent searches see the new email
    this.cache.clear();

    return this.http.post<{ success: boolean }>(url, {
      email: normalizedEmail,
    });
  }

  /**
   * Delete an email address from suggestions
   */
  deleteEmail(email: string): Observable<{ success: boolean }> {
    if (!email) {
      return of({ success: false });
    }

    const normalizedEmail = email.trim().toLowerCase();
    const url = generateURL({
      endpoint: ENDPOINTS.EMAIL_SUGGESTIONS_DELETE,
    });

    this.cache.clear();

    const params = new HttpParams().set('email', normalizedEmail);
    return this.http.delete<{ success: boolean }>(url, { params });
  }

  /**
   * RFC compliant basic email format validation
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test((email || '').trim());
  }
}
